(function () {
  if (customElements.get('country-directory')) return;

  const COUNT_CONCURRENCY = 2;
  const CACHE_TTL = 15 * 60 * 1000;
  const CACHE_VERSION = 2;
  const STOREFRONT_ENDPOINT = '/api/2026-07/graphql.json';
  const STOREFRONT_QUERY = `
    query CountryDirectoryCounts($cursor: String, $country: CountryCode)
      @inContext(country: $country) {
      products(first: 250, after: $cursor, sortKey: ID) {
        nodes {
          id
          availableForSale
          countries: metafield(namespace: "custom", key: "countries") {
            type
            value
          }
          sizeRecommend: metafield(namespace: "custom", key: "size_recommend") {
            type
            value
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  class CountryDirectory extends HTMLElement {
    cacheKey(kind) {
      const context = [
        window.Shopify?.theme?.id || 'theme',
        this.dataset.sizeMetafieldKey || 'size_recommend',
        window.Shopify?.country || 'KR',
        window.Shopify?.currency?.active || 'currency',
        globalThis.document?.documentElement?.lang || 'language',
      ].map((value) => encodeURIComponent(String(value))).join(':');
      return `fwd:country-directory:${kind}:v${CACHE_VERSION}:${context}`;
    }

    readSessionCache(kind) {
      try {
        const cached = JSON.parse(window.sessionStorage.getItem(this.cacheKey(kind)) || 'null');
        if (!cached || Date.now() - cached.cachedAt > CACHE_TTL) return null;
        return cached.value;
      } catch (_error) {
        return null;
      }
    }

    writeSessionCache(kind, value) {
      try {
        window.sessionStorage.setItem(this.cacheKey(kind), JSON.stringify({ cachedAt: Date.now(), value }));
      } catch (_error) {
        // The directory still works when browser storage is unavailable.
      }
    }

    serializeCounts(counts, sizeCatalog) {
      return {
        counts: Array.from(counts.entries()).map(([key, count]) => [key, {
          available: count.available,
          total: count.total,
          sizeIds: Array.from(count.sizeIds || []),
          products: Array.from(count.products || []).map((product) => ({
            id: product.id,
            available: Boolean(product.available),
            sizeIds: Array.from(product.sizeIds || []),
          })),
        }]),
        sizeCatalog: Array.from(sizeCatalog.entries()),
      };
    }

    deserializeCounts(cached) {
      if (!cached || !Array.isArray(cached.counts) || !Array.isArray(cached.sizeCatalog)) return null;
      try {
        const counts = new Map(cached.counts.map(([key, count]) => [key, {
          available: count.available,
          total: count.total,
          sizeIds: new Set(count.sizeIds || []),
          products: (count.products || []).map((product) => ({
            id: product.id,
            available: Boolean(product.available),
            sizeIds: new Set(product.sizeIds || []),
          })),
        }]));
        return { counts, sizeCatalog: new Map(cached.sizeCatalog) };
      } catch (_error) {
        return null;
      }
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        this.addSizeFilterDismissListeners();
        return;
      }

      this.dataset.initialized = 'true';
      this.items = Array.from(this.querySelectorAll('[data-country-item]'));
      this.list = this.querySelector('[data-country-list]');
      this.sortToggle = this.querySelector('[data-country-sort]');
      this.soldOutToggle = this.querySelector('[data-country-sold-out-toggle]');
      this.sizeFilter = this.querySelector('[data-country-size-filter]');
      this.sizeOptions = this.querySelector('[data-country-size-options]');
      this.sizeCount = this.querySelector('[data-country-size-count]');
      this.sizeClear = this.querySelector('[data-country-size-clear]');
      this.activeFilters = this.querySelector('[data-country-active-filters]');
      this.filterEmpty = this.querySelector('[data-country-filter-empty]');
      this.selectedSizeIds = new Set();

      this.restoreControlsFromUrl();
      this.sortToggle?.addEventListener('click', () => this.toggleSortMode());
      this.soldOutToggle?.addEventListener('click', () => this.toggleSoldOutCountries());
      this.sizeOptions?.addEventListener('change', (event) => {
        if (event.target.matches('input[type="checkbox"]')) this.updateSizeSelection();
      });
      this.sizeClear?.addEventListener('click', () => this.clearSizeSelection());
      this.activeFilters?.addEventListener('click', (event) => {
        const badge = event.target.closest('[data-country-size-badge]');
        if (!badge) return;
        const checkbox = Array.from(this.sizeOptions.querySelectorAll('input[type="checkbox"]'))
          .find((input) => input.value === badge.dataset.countrySizeBadge);
        if (checkbox) checkbox.checked = false;
        this.updateSizeSelection();
      });
      this.addSizeFilterDismissListeners();
      this.setCountControlsEnabled(false);
      this.sortList();
      this.loadCounts();
    }

    disconnectedCallback() {
      this.countRequest?.abort();
      this.removeSizeFilterDismissListeners();
    }

    addSizeFilterDismissListeners() {
      if (!this.sizeFilter || this.sizeFilterDismissListenersAdded) return;
      this.handleSizeFilterOutsideClick ||= (event) => {
        if (this.sizeFilter.open && !this.sizeFilter.contains(event.target)) {
          this.sizeFilter.removeAttribute('open');
        }
      };
      this.handleSizeFilterEscape ||= (event) => {
        if (event.key !== 'Escape' || !this.sizeFilter.open) return;
        this.sizeFilter.removeAttribute('open');
        this.sizeFilter.querySelector('summary')?.focus();
      };
      document.addEventListener('click', this.handleSizeFilterOutsideClick);
      document.addEventListener('keydown', this.handleSizeFilterEscape);
      this.sizeFilterDismissListenersAdded = true;
    }

    removeSizeFilterDismissListeners() {
      if (!this.sizeFilterDismissListenersAdded) return;
      document.removeEventListener('click', this.handleSizeFilterOutsideClick);
      document.removeEventListener('keydown', this.handleSizeFilterEscape);
      this.sizeFilterDismissListenersAdded = false;
    }

    sizeUrlValue(id) {
      return String(id).split('/').pop();
    }

    restoreControlsFromUrl() {
      const params = new URL(window.location.href).searchParams;
      this.urlSizeValues = new Set(params.getAll('size'));

      const sortByAvailable = params.get('sort') === 'available';
      if (this.sortToggle) {
        this.sortToggle.setAttribute('aria-pressed', String(sortByAvailable));
        this.sortToggle.value = sortByAvailable
          ? this.sortToggle.dataset.sortActiveValue
          : this.sortToggle.dataset.sortDefaultValue;
      }

      const hideSoldOut = params.get('sold_out') !== 'show';
      this.soldOutToggle?.setAttribute('aria-pressed', String(hideSoldOut));
    }

    updateUrlState() {
      const url = new URL(window.location.href);
      url.searchParams.delete('size');
      Array.from(this.selectedSizeIds)
        .map((id) => this.sizeUrlValue(id))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
        .forEach((value) => url.searchParams.append('size', value));

      if (this.sortToggle?.value === 'available') url.searchParams.set('sort', 'available');
      else url.searchParams.delete('sort');

      if (this.soldOutToggle?.getAttribute('aria-pressed') === 'true') {
        url.searchParams.delete('sold_out');
      } else {
        url.searchParams.set('sold_out', 'show');
      }
      window.history.replaceState(window.history.state, '', url);
    }

    storefrontToken() {
      const element = document.getElementById('shopify-features');
      if (!element) throw new Error('Shopify storefront features were not found');
      const features = JSON.parse(element.textContent);
      if (typeof features.accessToken !== 'string' || !features.accessToken) {
        throw new Error('Shopify public Storefront token was not found');
      }
      return features.accessToken;
    }

    metaobjectIds(metafield) {
      if (!metafield || typeof metafield.value !== 'string') return [];
      if (metafield.type === 'metaobject_reference') {
        return metafield.value.startsWith('gid://shopify/Metaobject/') ? [metafield.value] : [];
      }
      if (metafield.type !== 'list.metaobject_reference') return [];

      try {
        const ids = JSON.parse(metafield.value);
        if (!Array.isArray(ids)) return [];
        return ids.filter((id) => typeof id === 'string'
          && id.startsWith('gid://shopify/Metaobject/'));
      } catch (_error) {
        return [];
      }
    }

    productCountryIds(product) {
      return this.metaobjectIds(product.countries);
    }

    productSizeIds(product) {
      return this.metaobjectIds(product.sizeRecommend);
    }

    async fetchStorefrontPage(cursor, token, signal) {
      let lastError;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(new URL(STOREFRONT_ENDPOINT, window.location.origin), {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            signal,
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token,
            },
            body: JSON.stringify({
              query: this.dataset.sizeMetafieldKey === 'ai_size_recommend'
                ? STOREFRONT_QUERY.replace('key: "size_recommend"', 'key: "ai_size_recommend"')
                : STOREFRONT_QUERY,
              variables: {
                cursor,
                country: window.Shopify?.country || 'KR',
              },
            }),
          });
          if (!response.ok) throw new Error(`Storefront country counts failed: ${response.status}`);
          const payload = await response.json();
          if (Array.isArray(payload?.errors) && payload.errors.length) {
            throw new Error('Storefront country counts returned GraphQL errors');
          }
          const products = payload?.data?.products;
          if (!Array.isArray(products?.nodes)
            || typeof products?.pageInfo?.hasNextPage !== 'boolean'
            || (products.pageInfo.hasNextPage && typeof products.pageInfo.endCursor !== 'string')) {
            throw new Error('Storefront country counts response was incomplete');
          }
          return products;
        } catch (error) {
          if (signal.aborted) throw error;
          lastError = error;
          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, 400 * (2 ** attempt)));
          }
        }
      }
      throw lastError;
    }

    aggregateProducts(products) {
      const counts = new Map();
      const productIds = new Set();
      products.forEach((product) => {
        if (typeof product?.id !== 'string' || productIds.has(product.id)) return;
        productIds.add(product.id);
        const countryIds = new Set(this.productCountryIds(product));
        const sizeIds = new Set(this.productSizeIds(product));
        countryIds.forEach((countryId) => {
          const count = counts.get(countryId) || {
            available: 0,
            total: 0,
            sizeIds: new Set(),
            products: [],
          };
          count.total += 1;
          if (product.availableForSale) count.available += 1;
          sizeIds.forEach((id) => count.sizeIds.add(id));
          count.products.push({ id: product.id, available: product.availableForSale, sizeIds });
          counts.set(countryId, count);
        });
      });
      return counts;
    }

    async collectStorefrontCounts(signal) {
      const token = this.storefrontToken();
      const products = [];
      const cursors = new Set();
      let cursor = null;

      for (let page = 0; page < 20; page += 1) {
        const response = await this.fetchStorefrontPage(cursor, token, signal);
        products.push(...response.nodes);
        if (!response.pageInfo.hasNextPage) return this.aggregateProducts(products);
        const nextCursor = response.pageInfo.endCursor;
        if (!nextCursor || cursors.has(nextCursor)) {
          throw new Error('Storefront country count cursor did not advance');
        }
        cursors.add(nextCursor);
        cursor = nextCursor;
      }
      throw new Error('Storefront country counts exceeded the page safety limit');
    }

    countUrl(page) {
      const url = new URL(this.dataset.collectionUrl, window.location.origin);
      url.searchParams.set('view', 'country-counts');
      if (page > 1) url.searchParams.set('page', String(page));
      return url;
    }

    async fetchCountPage(page, signal) {
      let lastError;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(this.countUrl(page), {
            credentials: 'same-origin',
            cache: 'no-store',
            signal,
          });
          if (!response.ok) throw new Error(`Country counts page failed: ${response.status}`);
          const payload = await response.json();
          if (!Number.isInteger(payload?.page)
            || !Number.isInteger(payload?.pages)
            || payload.page !== page
            || !Array.isArray(payload.products)) {
            throw new Error('Country counts page was incomplete');
          }
          return payload;
        } catch (error) {
          if (signal.aborted) throw error;
          lastError = error;
          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, 400 * (2 ** attempt)));
          }
        }
      }
      throw lastError;
    }

    async collectCountPages(signal) {
      const first = await this.fetchCountPage(1, signal);
      const pages = [first];
      const queue = Array.from({ length: first.pages - 1 }, (_, index) => index + 2);
      const workers = Array.from({ length: Math.min(COUNT_CONCURRENCY, queue.length) }, async () => {
        while (queue.length) pages.push(await this.fetchCountPage(queue.shift(), signal));
      });
      await Promise.all(workers);
      if (pages.length !== first.pages || new Set(pages.map((page) => page.page)).size !== first.pages) {
        throw new Error('Country counts pagination was incomplete');
      }
      return pages;
    }

    aggregateFallbackCounts(pages) {
      const products = [];
      pages.forEach((page) => {
        page.products.forEach((product) => {
          products.push({
            id: product.id,
            availableForSale: Boolean(product.available),
            countries: {
              type: 'list.metaobject_reference',
              value: JSON.stringify(product.countryIds || []),
            },
            sizeRecommend: {
              type: 'list.metaobject_reference',
              value: JSON.stringify(product.sizeIds || []),
            },
          });
        });
      });
      return this.aggregateProducts(products);
    }

    async collectCounts(signal) {
      try {
        return await this.collectStorefrontCounts(signal);
      } catch (error) {
        if (signal.aborted) throw error;
        console.warn('Storefront country counts unavailable; using Liquid fallback.', error);
        return this.aggregateFallbackCounts(await this.collectCountPages(signal));
      }
    }

    sizeCatalogUrl() {
      const url = new URL(this.dataset.sizeCatalogUrl, window.location.origin);
      url.searchParams.set('view', 'brand-size-data');
      return url;
    }

    async collectSizeCatalog(signal) {
      let lastError;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(this.sizeCatalogUrl(), {
            credentials: 'same-origin',
            cache: 'no-store',
            signal,
          });
          if (!response.ok) throw new Error(`Country size catalog failed: ${response.status}`);
          const payload = await response.json();
          if (!Array.isArray(payload?.sizes)) throw new Error('Country size catalog was incomplete');
          return new Map(payload.sizes
            .filter((size) => typeof size?.id === 'string' && typeof size?.label === 'string')
            .map((size) => [size.id, size.label.trim()]));
        } catch (error) {
          if (signal.aborted) throw error;
          lastError = error;
          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, 400 * (2 ** attempt)));
          }
        }
      }
      throw lastError;
    }

    applyCounts(counts) {
      this.items.forEach((item) => {
        const count = counts.get(item.dataset.countryFilter) || {
          available: 0,
          total: 0,
          sizeIds: new Set(),
          products: [],
        };
        item.dataset.countryAvailable = String(count.available);
        item.dataset.countryTotal = String(count.total);
        item.countrySizeIds = new Set(count.sizeIds || []);
        item.countryProducts = Array.from(count.products || []);
        item.classList.remove('is-count-loading', 'is-count-error');
        item.classList.toggle('is-sold-out', count.available === 0);
        item.querySelector('[data-country-available-count]').textContent = String(count.available);
        item.querySelector('[data-country-total-count]').textContent = String(count.total);
      });
      this.updateDisplayedCounts();
      this.updateCountryLinks();
    }

    buildSizeFilter(counts, sizeCatalog) {
      if (!this.sizeFilter || !this.sizeOptions || !this.sizeCount || !this.sizeClear) return;
      const productCounts = new Map();
      const referencedIds = new Set();
      const productIds = new Set();
      counts.forEach((count) => {
        (count.products || []).forEach((product) => {
          if (productIds.has(product.id)) return;
          productIds.add(product.id);
          (product.sizeIds || []).forEach((id) => {
            referencedIds.add(id);
            productCounts.set(id, (productCounts.get(id) || 0) + 1);
          });
        });
      });

      const options = Array.from(sizeCatalog.entries())
        .filter(([id]) => referencedIds.has(id))
        .sort((left, right) => left[1].localeCompare(right[1], undefined, {
          numeric: true,
          sensitivity: 'base',
        }));
      this.sizeLabels = new Map(options);
      this.sizeOptions.replaceChildren(...options.map(([id, label]) => {
        const option = document.createElement('label');
        option.className = 'brands-directory__size-option';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.value = id;
        checkbox.checked = this.selectedSizeIds.has(id) || this.urlSizeValues.has(this.sizeUrlValue(id));
        const text = document.createElement('span');
        text.textContent = `${label} (${productCounts.get(id) || 0})`;
        option.append(checkbox, text);
        return option;
      }));
      this.sizeFilter.hidden = options.length === 0;
      this.updateSizeSelection();
    }

    updateSizeSelection() {
      this.selectedSizeIds = new Set(Array.from(
        this.sizeOptions?.querySelectorAll('input[type="checkbox"]:checked') || []
      ).map((checkbox) => checkbox.value));
      if (this.sizeCount) {
        this.sizeCount.textContent = String(this.selectedSizeIds.size);
        this.sizeCount.hidden = this.selectedSizeIds.size === 0;
      }
      if (this.sizeClear) this.sizeClear.disabled = this.selectedSizeIds.size === 0;
      this.updateDisplayedCounts();
      this.updateActiveFilterBadges();
      this.updateCountryLinks();
      this.updateSoldOutVisibility();
      this.updateUrlState();
    }

    updateActiveFilterBadges() {
      if (!this.activeFilters) return;
      const badges = Array.from(this.selectedSizeIds).map((id) => {
        const badge = document.createElement('button');
        badge.className = 'brands-directory__filter-badge';
        badge.type = 'button';
        badge.dataset.countrySizeBadge = id;
        const label = this.sizeLabels?.get(id) || this.sizeUrlValue(id);
        badge.textContent = `${label} ×`;
        badge.setAttribute('aria-label', `${label} 필터 제거`);
        return badge;
      });
      this.activeFilters.replaceChildren(...badges);
      this.activeFilters.hidden = badges.length === 0;
    }

    updateCountryLinks() {
      this.items.forEach((item) => {
        const link = item.querySelector('.brands-directory__brand-link');
        if (!link) return;
        const url = new URL(item.dataset.countryUrl, window.location.origin);
        url.searchParams.delete(this.dataset.sizeFilterParam);
        Array.from(this.selectedSizeIds)
          .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
          .forEach((id) => url.searchParams.append(this.dataset.sizeFilterParam, id));
        link.href = url;
      });
    }

    productMatchesSelectedSize(product) {
      return Array.from(this.selectedSizeIds)
        .some((id) => product.sizeIds?.has(id));
    }

    selectedAvailableCount(item) {
      return (item.countryProducts || []).filter((product) => product.available
        && this.productMatchesSelectedSize(product)).length;
    }

    updateDisplayedCounts() {
      const hasSizeFilter = this.selectedSizeIds.size > 0;
      this.items.forEach((item) => {
        const available = Number(item.dataset.countryAvailable);
        const total = Number(item.dataset.countryTotal);
        if (available < 0 || total < 0) return;
        const filteredTotal = hasSizeFilter
          ? this.selectedAvailableCount(item)
          : available;
        item.querySelector('[data-country-available-count]').textContent = String(filteredTotal);
        item.querySelector('[data-country-total-count]').textContent = String(total);
        const counts = item.querySelector('[data-country-counts]');
        if (counts) counts.title = hasSizeFilter ? counts.dataset.filteredTitle : counts.dataset.defaultTitle;
      });
    }

    clearSizeSelection() {
      this.sizeOptions?.querySelectorAll('input[type="checkbox"]:checked')
        .forEach((checkbox) => { checkbox.checked = false; });
      this.updateSizeSelection();
    }

    setCountControlsEnabled(enabled) {
      if (this.sortToggle) this.sortToggle.disabled = !enabled;
      if (this.soldOutToggle) this.soldOutToggle.disabled = !enabled;
    }

    toggleSortMode() {
      if (!this.sortToggle || this.sortToggle.disabled) return;
      const isActive = this.sortToggle.getAttribute('aria-pressed') === 'true';
      this.sortToggle.setAttribute('aria-pressed', String(!isActive));
      this.sortToggle.value = isActive
        ? this.sortToggle.dataset.sortDefaultValue
        : this.sortToggle.dataset.sortActiveValue;
      this.sortList();
      this.updateUrlState();
    }

    toggleSoldOutCountries() {
      if (!this.soldOutToggle || this.soldOutToggle.disabled) return;
      const shouldHide = this.soldOutToggle.getAttribute('aria-pressed') !== 'true';
      this.soldOutToggle.setAttribute('aria-pressed', String(shouldHide));
      this.updateSoldOutVisibility();
      this.updateUrlState();
    }

    updateSoldOutVisibility() {
      const shouldHide = this.soldOutToggle?.getAttribute('aria-pressed') === 'true';
      const hasSizeFilter = this.selectedSizeIds.size > 0;
      let visibleCount = 0;
      this.items.forEach((item) => {
        const matchesSize = !hasSizeFilter || Array.from(this.selectedSizeIds)
          .some((id) => item.countrySizeIds?.has(id));
        const selectedSizeSoldOut = hasSizeFilter && this.selectedAvailableCount(item) === 0;
        item.hidden = (shouldHide
          && (item.classList.contains('is-sold-out') || selectedSizeSoldOut)) || !matchesSize;
        if (!item.hidden) visibleCount += 1;
      });
      if (this.filterEmpty) this.filterEmpty.hidden = !hasSizeFilter || visibleCount > 0;
    }

    normalizedCountryLabel(label) {
      return String(label)
        .replace(/^[\p{Extended_Pictographic}\p{Regional_Indicator}\uFE0F\u200D\s]+/gu, '')
        .trim();
    }

    sortList() {
      if (!this.list) return;
      const mode = this.sortToggle?.value || 'alphabetical';
      const items = Array.from(this.list.querySelectorAll('[data-country-item]'));
      items.sort((left, right) => {
        if (mode === 'available') {
          const difference = Number(right.dataset.countryAvailable) - Number(left.dataset.countryAvailable);
          if (difference !== 0) return difference;
        }
        return this.normalizedCountryLabel(left.dataset.countryLabel)
          .localeCompare(this.normalizedCountryLabel(right.dataset.countryLabel), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
      });
      this.list.append(...items);
    }

    showCountError() {
      this.items.forEach((item) => {
        item.dataset.countryAvailable = '-1';
        item.dataset.countryTotal = '-1';
        item.classList.remove('is-count-loading', 'is-sold-out');
        item.classList.add('is-count-error');
        item.querySelector('[data-country-available-count]').textContent = this.dataset.errorLabel;
        item.querySelector('[data-country-total-count]').textContent = this.dataset.errorLabel;
      });
    }

    async loadCounts() {
      this.countRequest?.abort();
      const request = new AbortController();
      this.countRequest = request;
      this.setAttribute('aria-busy', 'true');
      this.setCountControlsEnabled(false);
      try {
        const cached = this.deserializeCounts(this.readSessionCache('counts'));
        let counts;
        let sizeCatalog;
        if (cached) {
          ({ counts, sizeCatalog } = cached);
        } else {
          [counts, sizeCatalog] = await Promise.all([
            this.collectCounts(request.signal),
            this.collectSizeCatalog(request.signal).catch((error) => {
              if (!request.signal.aborted) console.error(error);
              return new Map();
            }),
          ]);
          this.writeSessionCache('counts', this.serializeCounts(counts, sizeCatalog));
        }
        if (this.countRequest !== request || request.signal.aborted) return;
        this.applyCounts(counts);
        this.buildSizeFilter(counts, sizeCatalog);
        this.setCountControlsEnabled(true);
        this.sortList();
        this.updateSoldOutVisibility();
      } catch (error) {
        if (this.countRequest !== request || request.signal.aborted) return;
        this.showCountError();
        console.error(error);
      } finally {
        if (this.countRequest === request) this.setAttribute('aria-busy', 'false');
      }
    }
  }

  customElements.define('country-directory', CountryDirectory);
})();
